import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UserRole } from '../../types/organization.types';
import { ManagedUser } from '../../types/auth.types';
import { useCompanyOptions, useDepartmentOptions, useTeamOptions } from '../../hooks/useOrgQueries';
import {
  useCreateUserRole,
  useUpdateUserRole,
  useDeleteUserRole,
  UpdateUserRolePayload,
  UserRoleOrgBinding,
} from '../../hooks/useUserQueries';
import { LedgerDialog, LedgerField, LedgerSelect, LedgerSelectOption } from '../ui';

type OrgLevel = 'company' | 'team' | 'department';
type RoleValue = 'ADMIN' | 'MANAGER' | 'USER';

const schema = yup.object({
  role: yup.string().default('').required('Role is required'),
  level: yup.string().defined().default('company'),
  organization: yup.string().default('').required('Organization is required'),
});

type RoleAssignmentFormValues = yup.InferType<typeof schema>;

const defaultValues: RoleAssignmentFormValues = {
  role: '',
  level: 'company',
  organization: '',
};

const roleOptions: LedgerSelectOption[] = [
  { value: '', label: 'Select a role' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'USER', label: 'User' },
];

const levelOptions: LedgerSelectOption[] = [
  { value: 'company', label: 'Company' },
  { value: 'team', label: 'Team' },
  { value: 'department', label: 'Department' },
];

// Role rows backfill their parent chain (a team-bound row also carries the
// company), so the binding level is the MOST specific org key present.
const levelOf = (row: UserRole): OrgLevel =>
  row.department ? 'department' : row.team ? 'team' : 'company';

const bindingFor = (level: OrgLevel, orgId: string): UserRoleOrgBinding =>
  level === 'department' ? { department: orgId } : level === 'team' ? { team: orgId } : { company: orgId };

interface RoleAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: ManagedUser;
  /** null/undefined = create a new assignment for the user; set = edit that row. */
  role?: UserRole | null;
}

export const RoleAssignmentDialog: React.FC<RoleAssignmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
  role,
}) => {
  const [error, setError] = useState<string | null>(null);
  const roleTriggerRef = useRef<HTMLButtonElement>(null);

  const createRole = useCreateUserRole();
  const updateRole = useUpdateUserRole();
  const deleteRole = useDeleteUserRole();
  const saving = createRole.isPending || updateRole.isPending || deleteRole.isPending;

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RoleAssignmentFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });

  const level = watch('level') as OrgLevel;

  // Each level's option list fetches only while the dialog is open AND that
  // level is the active one, so switching levels lazy-loads on demand.
  const companiesQuery = useCompanyOptions(open && level === 'company');
  const teamsQuery = useTeamOptions(open && level === 'team');
  const departmentsQuery = useDepartmentOptions(open && level === 'department');

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    if (role) {
      reset({
        role: role.role,
        level: levelOf(role),
        organization: role.department ?? role.team ?? role.company ?? '',
      });
    } else {
      reset(defaultValues);
    }
  }, [role, open, reset]);

  const orgOptions: LedgerSelectOption[] = [
    { value: '', label: 'Select an organization' },
    ...(level === 'company'
      ? (companiesQuery.data ?? []).map((company) => ({ value: company.id, label: company.name }))
      : level === 'team'
        ? (teamsQuery.data ?? []).map((team) => ({
            value: team.id,
            label: `${team.name} (${team.companyName})`,
          }))
        : (departmentsQuery.data ?? []).map((department) => ({
            value: department.id,
            label: `${department.name} (${department.teamName})`,
          }))),
  ];

  // Inactive levels' queries are disabled and stay "pending" forever, so only
  // the active level's may hold the submit button hostage.
  const optionsPending =
    (level === 'company' && companiesQuery.isPending) ||
    (level === 'team' && teamsQuery.isPending) ||
    (level === 'department' && departmentsQuery.isPending);
  const optionsError =
    (level === 'company' && companiesQuery.isError) ||
    (level === 'team' && teamsQuery.isError) ||
    (level === 'department' && departmentsQuery.isError);

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const onSubmit = async (values: RoleAssignmentFormValues) => {
    setError(null);
    const roleValue = values.role as RoleValue;
    const nextLevel = values.level as OrgLevel;
    const binding = bindingFor(nextLevel, values.organization);
    // Tracks the rebind's point of no return: once the delete has settled,
    // a create failure leaves the user without the original assignment, and
    // the error strip must say so instead of a generic save failure.
    let originalRemoved = false;
    try {
      if (role) {
        if (nextLevel === levelOf(role)) {
          // Same-level edits PATCH, echoing the binding key — the serializer
          // re-validates exactly-one-org on partial updates too.
          const data: UpdateUserRolePayload = { role: roleValue, ...binding };
          await updateRole.mutateAsync({ id: role.id, data });
        } else {
          // The API rejects cross-level rebinds on PATCH; moving a role
          // between levels is delete-then-recreate.
          await deleteRole.mutateAsync(role.id);
          originalRemoved = true;
          await createRole.mutateAsync({ email: user.email, role: roleValue, ...binding });
        }
      } else {
        await createRole.mutateAsync({ email: user.email, role: roleValue, ...binding });
      }
      onSuccess();
      handleClose();
    } catch (err) {
      if (originalRemoved) {
        setError(
          'The original assignment was removed, but creating the new one failed — add it again.',
        );
        return;
      }
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || 'Failed to save role assignment');
    }
  };

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={role ? 'Edit Role Assignment' : 'Add Role Assignment'}
      size="md"
      // isSubmitting spans the whole delete-then-create rebind, covering the
      // single-frame gap where neither mutation is pending yet.
      preventClose={saving || isSubmitting}
      initialFocus={roleTriggerRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving || isSubmitting}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="role-assignment-form"
            disabled={saving || isSubmitting || optionsPending}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Saving…' : role ? 'Update' : 'Create'}
          </button>
        </>
      }
    >
      <form id="role-assignment-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {(error || optionsError) && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error ?? 'Failed to load organizations'}
          </p>
        )}

        <LedgerField label="Role" error={errors.role?.message}>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <LedgerSelect
                ariaLabel="Role"
                value={field.value}
                options={roleOptions}
                onValueChange={field.onChange}
                triggerRef={roleTriggerRef}
              />
            )}
          />
        </LedgerField>

        <LedgerField label="Level">
          <Controller
            name="level"
            control={control}
            render={({ field }) => (
              <LedgerSelect
                ariaLabel="Level"
                value={field.value}
                options={levelOptions}
                onValueChange={(next) => {
                  // A level switch invalidates the previous selection — it
                  // belongs to another org collection entirely.
                  if (next !== field.value) setValue('organization', '');
                  field.onChange(next);
                }}
              />
            )}
          />
        </LedgerField>

        <LedgerField label="Organization" error={errors.organization?.message}>
          <Controller
            name="organization"
            control={control}
            render={({ field }) => (
              <LedgerSelect
                ariaLabel="Organization"
                value={field.value}
                options={orgOptions}
                onValueChange={field.onChange}
                disabled={optionsPending}
              />
            )}
          />
        </LedgerField>
      </form>
    </LedgerDialog>
  );
};
